'use client';

import React, { useState, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Image from 'next/image';
import TrustpilotWidget from '@/components/TrustpilotWidget';

const whyMattersCards = [
  {
    title: 'Replacement modules come blank',
    text: "They carry no vehicle data and won't function until programmed with your specific vehicle configuration.",
  },
  {
    title: 'Incorrect coding causes failures',
    text: 'Persistent warning lights, system failures, and MOT failures result from improperly coded replacement modules.',
  },
  {
    title: 'Security-critical modules',
    text: "Immobilisers and keyless systems require exact coding — incorrect programming means the vehicle simply won\'t start.",
  },
  {
    title: 'ADAS needs calibration after coding',
    text: 'Cameras, radar, and lane assist systems must be verified and calibrated post-programming to function correctly.',
  },
];

const modules = [
  'Airbag modules (SRS control units)',
  'Engine control units (ECU / DME / DDE)',
  'Transmission control units (TCU / EGS)',
  'Steering angle sensors',
  'ABS / DSC modules',
  'Comfort modules (windows, mirrors, seats, lighting)',
  'Keyless entry and immobiliser systems (EIS / CAS / FEM)',
  'Parking sensors, cameras, and ADAS modules',
];

const brands = [
  'Mercedes-Benz (all models)',
  'BMW (all models including MINI)',
  'Audi',
  'Volkswagen',
  'Porsche',
  'SEAT',
  'Škoda',
];

const equipmentBadges = [
  'Mercedes-Benz XENTRY',
  'BMW ISTA',
  'VAG ODIS',
  'Autel Ultra',
];

const scenarios = [
  {
    num: '01',
    title: 'Post-accident airbag module replacement',
    text: 'SRS module replacement and coding after deployment — all components verified fault-free.',
  },
  {
    num: '02',
    title: 'ECU failure or replacement',
    text: 'Including virginising used units and recoding to your specific vehicle configuration.',
  },
  {
    num: '03',
    title: 'Used module installation',
    text: 'Requires virgin/blank process and full recode to vehicle — we handle the complete procedure.',
  },
  {
    num: '04',
    title: 'Battery replacement registration',
    text: 'Mercedes and BMW vehicles require battery registration after replacement to protect the charging system.',
  },
  {
    num: '05',
    title: 'Steering angle sensor calibration',
    text: 'Required after alignment or suspension work — ensures correct stability control operation.',
  },
  {
    num: '06',
    title: 'ADAS calibration after windscreen replacement',
    text: 'Front camera and radar recalibration after windscreen or front-end repair.',
  },
];

const processSteps = [
  {
    num: '01',
    title: 'Diagnostic Scan',
    text: 'Full system scan to identify fault codes, module variants, and programming requirements.',
  },
  {
    num: '02',
    title: 'Parts Sourcing',
    text: 'If a replacement module is needed, we source the correct part number for your specific vehicle.',
  },
  {
    num: '03',
    title: 'Module Coding',
    text: "Using factory-level tools, we program the module with your vehicle's exact configuration data.",
  },
  {
    num: '04',
    title: 'System Verification',
    text: 'All related systems are tested to confirm correct communication and zero fault codes.',
  },
  {
    num: '05',
    title: 'Final Clearance',
    text: 'Any residual fault codes are cleared and the vehicle is road-tested if required.',
  },
];

const ecuNodes = [
  { label: 'ECU', sub: 'Engine' },
  { label: 'TCU', sub: 'Transmission' },
  { label: 'SRS', sub: 'Airbag' },
  { label: 'ABS', sub: 'Braking' },
  { label: 'EIS', sub: 'Immobiliser' },
  { label: 'ADAS', sub: 'Cameras' },
];

const geoBg = {
  background: 'linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #0d0d0d 100%)',
};

const geoTexture = {
  backgroundImage: `repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 28px,
    rgba(255,255,255,0.018) 28px,
    rgba(255,255,255,0.018) 29px
  )`,
};

const altBg = {
  background: 'linear-gradient(135deg, #0d0d0d 0%, #111111 100%)',
};

function GeoDivider() {
  return (
    <div className="relative h-px overflow-visible" aria-hidden="true">
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(204,26,26,0.3) 30%, rgba(204,26,26,0.3) 70%, transparent 100%)' }}
      />
      <div
        className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4"
        style={{ background: '#CC1A1A', clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)', opacity: 0.5 }}
      />
    </div>
  );
}

export default function ModuleCodingPage() {
  // ── Booking form state ──────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    registration: '',
    vehicleMake: '',
    codingIssue: '',
    website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showModal, setShowModal] = useState(false);
  const [bookingReference, setBookingReference] = useState('');
  const [rateLimitMsg, setRateLimitMsg] = useState('');
  const lastSubmitRef = React.useRef<number>(0);

  const validateField = (name: string, value: string): string => {
    if (name === 'name') {
      if (!value.trim()) return 'Full name is required';
      if (value.trim().length < 2) return 'Name must be at least 2 characters';
    }
    if (name === 'phone') {
      if (!value.trim()) return 'Phone number is required';
      const stripped = value.replace(/[\s\-().]/g, '');
      if (!/^(\+44|0044|0)[1-9]\d{8,9}$/.test(stripped)) return 'Enter a valid UK phone number';
    }
    if (name === 'registration') {
      if (!value.trim()) return 'Vehicle registration is required';
    }
    if (name === 'vehicleMake') {
      if (!value.trim()) return 'Vehicle make & model is required';
    }
    if (name === 'codingIssue') {
      if (!value.trim()) return 'Please describe the module or coding issue';
    }
    if (name === 'email' && value.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Enter a valid email address';
    }
    return '';
  };

  const handleFieldChange = (name: string, value: string) => {
    setFormData(p => ({ ...p, [name]: name === 'registration' ? value.toUpperCase() : value }));
    if (touched[name]) {
      const err = validateField(name, value);
      setFieldErrors(prev => { const next = { ...prev }; if (err) next[name] = err; else delete next[name]; return next; });
    }
  };

  const handleFieldBlur = (name: string, value: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setFieldErrors(prev => { const next = { ...prev }; if (err) next[name] = err; else delete next[name]; return next; });
  };

  const inputClass = (name: string) => {
    const base = 'w-full px-4 py-3 bg-[#1a1a1a] border text-white placeholder-white/30 font-dm-sans text-sm focus:outline-none transition-colors rounded-sm';
    if (touched[name] && fieldErrors[name]) return `${base} border-red-500 focus:border-red-500`;
    if (touched[name] && !fieldErrors[name]) return `${base} border-green-600/60 focus:border-green-600/60`;
    return `${base} border-white/10 focus:border-[#CC1A1A]/60`;
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // ── Rate limiting ────────────────────────────────────────────────────────
    const now = Date.now();
    if (now - lastSubmitRef.current < 60000) {
      const remaining = Math.ceil((60000 - (now - lastSubmitRef.current)) / 1000);
      setRateLimitMsg(`Please wait ${remaining} seconds before submitting again.`);
      return;
    }
    setRateLimitMsg('');

    // ── Honeypot check ───────────────────────────────────────────────────────
    if (formData.website) {
      setShowModal(true);
      return;
    }

    // Validate all required fields
    const requiredFields = ['name', 'phone', 'registration', 'vehicleMake', 'codingIssue'];
    const allTouched: Record<string, boolean> = {};
    const errors: Record<string, string> = {};
    requiredFields.forEach(f => {
      allTouched[f] = true;
      const err = validateField(f, formData[f as keyof typeof formData] as string);
      if (err) errors[f] = err;
    });
    if (formData.email.trim()) {
      const emailErr = validateField('email', formData.email);
      if (emailErr) { errors.email = emailErr; allTouched.email = true; }
    }
    setTouched(prev => ({ ...prev, ...allTouched }));
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    setFormError('');
    lastSubmitRef.current = Date.now();

    const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    // ── Generate booking reference ────────────────────────────────────────────
    const year = new Date().getFullYear();
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const rand = Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    const bookingRef = `NLB-${year}-${rand}`;

    // ── Save to Supabase (independent of email) ──────────────────────────────
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      await supabase.from('bookings').insert({
        service_type: 'module-coding',
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        vehicle_reg: formData.registration.toUpperCase(),
        vehicle_make_model: formData.vehicleMake,
        message: formData.codingIssue || null,
        photo_urls: null,
        status: 'new',
        reference: bookingRef,
      });
    } catch {
      // Supabase save failure must not block email
    }

    // ── Send email (independent of Supabase) ─────────────────────────────────
    try {
      const body = new FormData();
      body.append('name', formData.name);
      body.append('phone', formData.phone);
      body.append('email', formData.email);
      body.append('registration', formData.registration.toUpperCase());
      body.append('vehicleMake', formData.vehicleMake);
      body.append('codingIssue', formData.codingIssue);
      body.append('service', 'Module Coding');
      body.append('subject', `New Module Coding Booking – ${formData.name} – ${formData.registration.toUpperCase()}`);
      body.append('successMessage', 'Thank you! A coding specialist will call you within 1 hour.');
      body.append('bookingRef', bookingRef);

      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-booking-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        body,
      });
      if (!res.ok) throw new Error('Email failed');
    } catch {
      // Email failure must not block success if Supabase saved
    }

    setFormData({ name: '', phone: '', email: '', registration: '', vehicleMake: '', codingIssue: '', website: '' });
    setFieldErrors({});
    setTouched({});
    setSubmitting(false);
    setBookingReference(bookingRef);
    setShowModal(true);
  }, [formData]);

  const handleCloseModal = () => {
    setShowModal(false);
    if (typeof window !== 'undefined') {
      window.location.href = `/booking-confirmed?service=module-coding&ref=${encodeURIComponent(bookingReference)}`;
    }
  };

  return (
    <>
      {/* ─── SUCCESS MODAL ─── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-heading-mc"
        >
          <div
            className="relative w-full max-w-md rounded-sm p-8 flex flex-col items-center text-center"
            style={{ background: '#121212', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ background: 'rgba(22,163,74,0.15)', border: '2px solid rgba(22,163,74,0.5)' }}
              aria-hidden="true"
            >
              <svg
                width="40" height="40" viewBox="0 0 24 24" fill="none"
                stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: 'checkPop 0.4s ease-out' }}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 id="modal-heading-mc" className="font-dm-sans font-black text-white text-2xl mb-3">
              Booking Received!
            </h2>
            <p className="font-dm-sans text-white/60 text-[15px] leading-relaxed mb-4">
              Thank you! A coding specialist will call you within 1 hour.
            </p>
            {bookingReference && (
              <div
                className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-sm"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <span className="font-dm-sans text-[11px] uppercase tracking-[0.2em] text-white/35">Reference</span>
                <span className="font-dm-sans text-[13px] font-bold text-white/80 tracking-wider">{bookingReference}</span>
              </div>
            )}
            <button
              onClick={handleCloseModal}
              className="w-full py-3 font-dm-sans font-bold text-[13px] uppercase tracking-[0.15em] text-white transition-all duration-200 rounded-sm"
              style={{ background: '#CC1A1A' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
              onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <style>{`@keyframes checkPop { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }`}</style>
      <Header />
      <main className="min-h-screen" style={geoBg}>
        {/* Global geometric texture overlay */}
        <div className="fixed inset-0 pointer-events-none z-0" style={geoTexture} aria-hidden="true" />

        {/* HERO */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden z-10">
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{ background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(204,26,26,0.07) 0%, transparent 70%)' }}
          />
          <div className="relative max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left: Text — with red left-border accent */}
              <div className="pl-6" style={{ borderLeft: '3px solid #CC1A1A' }}>
                <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#CC1A1A]/40 bg-[#CC1A1A]/10 mb-8">
                  <span className="w-2 h-2 rounded-full bg-[#CC1A1A] animate-pulse" />
                  <span className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A]">
                    Dealer-Level Programming
                  </span>
                </div>
                <h1 className="font-fraunces font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-3 tracking-tight">
                  Expert Module Coding for{' '}
                  <em className="text-[#CC1A1A] not-italic">German Vehicles</em>
                </h1>
                <div className="w-16 h-[3px] bg-[#CC1A1A] mb-6" />
                <p className="font-dm-sans text-[#C0C0C0] text-lg leading-relaxed mb-10 max-w-xl">
                  We program replacement control units to work perfectly with your car — no errors, no warning lights.
                </p>
                <div className="flex flex-wrap gap-4 mb-10">
                  <a
                    href="#booking-form"
                    onClick={e => { e.preventDefault(); document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' }); }}
                    className="inline-flex items-center gap-2 px-8 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                    style={{ background: '#CC1A1A', borderRadius: '3px' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
                  >
                    Book Diagnostic
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </a>
                  <a
                    href="tel:01614001687"
                    className="inline-flex items-center gap-2 px-8 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                    style={{ background: '#1a1a1a', border: '1px solid #CC1A1A', borderRadius: '3px' }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(204,26,26,0.7)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '#CC1A1A')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                    </svg>
                    01614001687
                  </a>
                </div>

              </div>

              {/* Right: Image — keep existing illustration */}
              <div className="relative">
                <div className="aspect-[4/3] bg-[#111111] border border-white/[0.08] overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  <Image
                    src="/assets/images/module_coding_hero.png"
                    alt="Isometric illustration of dealer-level module coding and ECU programming for luxury vehicles"
                    width={800}
                    height={600}
                    className="w-full h-full object-cover"
                    priority
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#CC1A1A]" />
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* WHAT IS MODULE CODING */}
        <section className="py-20 px-6 relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Technical Explainer</p>
                <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                  What is module coding?
                </h2>
                <div className="w-12 h-[3px] bg-[#CC1A1A] mb-6" />
                <p className="font-dm-sans text-[#C0C0C0] text-base leading-relaxed">
                  Modern vehicles contain dozens of electronic control units (ECUs), each managing a specific system — from airbags and engine management to comfort features and parking sensors. When any of these modules is replaced following an accident or failure, it arrives as a blank unit. Without vehicle-specific programming, it won&apos;t communicate with the rest of the car. Coding writes the correct data to the module so it functions exactly as the manufacturer intended — for your specific vehicle, trim level, and configuration.
                </p>
              </div>
              <div className="bg-[#111111] border border-[#CC1A1A]/30 p-8">
                <p className="font-dm-sans text-xs font-bold tracking-[0.2em] uppercase text-[#CC1A1A] mb-6">ECU Network</p>
                <div className="grid grid-cols-3 gap-3">
                  {ecuNodes?.map((item) => (
                    <div key={item?.label} className="bg-[#0a0a0a] border border-white/[0.08] p-3 flex flex-col items-center gap-1 hover:border-[#CC1A1A]/30 transition-colors">
                      <div className="w-2 h-2 rounded-full bg-[#CC1A1A] mb-1" />
                      <p className="font-dm-sans text-white text-sm font-bold">{item?.label}</p>
                      <p className="font-dm-sans text-[#C0C0C0] text-xs">{item?.sub}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-5 border-t border-white/[0.06]">
                  <p className="font-dm-sans text-[#C0C0C0] text-xs leading-relaxed">
                    Each module must be coded to communicate correctly with the vehicle&apos;s CAN bus network.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* WHY CODING MATTERS */}
        <section className="py-20 px-6 relative z-10" style={geoBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Critical Information</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Why coding matters
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyMattersCards?.map((card) => (
                <div key={card?.title} className="bg-[#111111] border border-white/[0.08] p-7 hover:border-[#CC1A1A]/30 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#CC1A1A]/10 flex items-center justify-center mb-5">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                  </div>
                  <h3 className="font-fraunces font-semibold text-white text-lg mb-3 leading-snug">{card?.title}</h3>
                  <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">{card?.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* MODULES WE WORK WITH */}
        <section className="py-20 px-6 relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Full Coverage</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Modules we work with
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {modules?.map((mod) => (
                <div key={mod} className="flex items-start gap-4 bg-[#111111] border border-white/[0.08] px-5 py-5 hover:border-[#CC1A1A]/30 transition-colors duration-300">
                  <div className="shrink-0 w-10 h-10 bg-[#CC1A1A]/10 flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed pt-2">{mod}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* VEHICLES */}
        <section className="py-20 px-6 relative z-10" style={geoBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Brand Specialists</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Vehicles we specialise in
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {brands?.map((brand) => (
                <div key={brand} className="bg-[#111111] border border-white/[0.08] px-6 py-5 flex items-center gap-3 hover:border-[#CC1A1A]/30 transition-colors duration-300">
                  <div className="w-2 h-2 rounded-full bg-[#CC1A1A] shrink-0" />
                  <span className="font-dm-sans text-[#E0E0E0] text-sm font-semibold">{brand}</span>
                </div>
              ))}
            </div>
            <div className="text-center">
              <p className="font-dm-sans text-white/40 text-sm italic">
                &quot;We use manufacturer-specific protocols for each brand — not generic aftermarket tools.&quot;
              </p>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* EQUIPMENT */}
        <section className="py-20 px-6 relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Professional Grade</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Dealer-level equipment
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>
            <div className="bg-[#111111] border border-[#CC1A1A]/30 p-10 max-w-4xl mx-auto">
              <p className="font-fraunces font-light text-white text-xl md:text-2xl leading-relaxed mb-8 text-center">
                We carry the same diagnostic and programming tools used by main dealers — manufacturer-licensed software, brand-specific interfaces, and live data connections to OEM servers where required.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                {equipmentBadges?.map((badge) => (
                  <div
                    key={badge}
                    className="px-5 py-2.5 bg-[#0a0a0a] border border-[#CC1A1A]/40 font-dm-sans text-[#CC1A1A] text-xs font-bold tracking-[0.15em] uppercase"
                  >
                    {badge}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* COMMON SCENARIOS */}
        <section className="py-20 px-6 relative z-10" style={geoBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Real-World Examples</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Common scenarios we handle
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {scenarios?.map((scenario) => (
                <div key={scenario?.num} className="bg-[#111111] border border-white/[0.08] p-7 relative hover:border-[#CC1A1A]/30 transition-colors duration-300">
                  <div className="inline-flex items-center justify-center w-9 h-9 bg-[#CC1A1A] font-dm-sans text-white text-xs font-bold mb-5">
                    {scenario?.num}
                  </div>
                  <h3 className="font-fraunces font-semibold text-white text-lg mb-3 leading-snug">{scenario?.title}</h3>
                  <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">{scenario?.text}</p>
                  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#CC1A1A] to-transparent" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* PROCESS */}
        <section className="py-20 px-6 relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Step by Step</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Our process
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {processSteps?.map((step) => (
                <div key={step?.num} className="bg-[#111111] border border-white/[0.08] p-7 relative hover:border-[#CC1A1A]/30 transition-colors duration-300">
                  <div className="font-fraunces text-5xl font-light text-[#CC1A1A] mb-5 leading-none">{step?.num}</div>
                  <h3 className="font-fraunces font-semibold text-white text-lg mb-3 leading-snug">{step?.title}</h3>
                  <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">{step?.text}</p>
                  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#CC1A1A] to-transparent" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* PHOTO PLACEHOLDERS */}
        <section className="py-20 px-6 relative z-10" style={geoBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Proof of Quality</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Programming done right
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative">
                <div className="aspect-[4/3] bg-[#111111] border border-white/[0.08] flex flex-col items-center justify-center gap-4">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" opacity={0.2}>
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                  <p className="font-dm-sans text-white/20 text-xs tracking-wider uppercase">Diagnostics in progress</p>
                </div>
                <div className="absolute top-4 left-4 px-3 py-1 bg-[#CC1A1A] font-dm-sans text-white text-xs font-bold uppercase tracking-widest">
                  Diagnostics in progress
                </div>
              </div>
              <div className="relative">
                <div className="aspect-[4/3] bg-[#111111] border border-white/[0.08] flex flex-col items-center justify-center gap-4">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" opacity={0.2}>
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                  <p className="font-dm-sans text-white/20 text-xs tracking-wider uppercase">Module verified</p>
                </div>
                <div className="absolute top-4 left-4 px-3 py-1 bg-[#161616] border border-white/20 font-dm-sans text-white text-xs font-bold uppercase tracking-widest">
                  Module verified
                </div>
              </div>
            </div>
            <p className="font-dm-sans text-white/30 text-sm text-center mt-6">
              Every module is verified communicating correctly before the vehicle leaves our workshop.
            </p>
          </div>
        </section>

        <GeoDivider />

        {/* CTA */}
        <section id="contact" className="py-24 px-6 relative z-10" style={altBg}>
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-6">Get in Touch</p>
            <h2 className="font-fraunces font-bold text-white text-3xl md:text-5xl leading-tight mb-2">
              Warning light on? Need a module coded?
            </h2>
            <div className="w-16 h-[3px] bg-[#CC1A1A] mx-auto mb-6" />
            <p className="font-dm-sans text-[#C0C0C0] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              We&apos;ll diagnose the issue and program your replacement module to factory specification.
            </p>
            <div className="flex flex-wrap gap-4 justify-center mb-10">
              <a
                href="#booking-form"
                onClick={e => { e.preventDefault(); document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 px-10 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                style={{ background: '#CC1A1A', borderRadius: '3px' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
                onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
              >
                Book Diagnostic
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a
                href="tel:01614001687"
                className="inline-flex items-center gap-2 px-10 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                style={{ background: '#1a1a1a', border: '1px solid #CC1A1A', borderRadius: '3px' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(204,26,26,0.7)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = '#CC1A1A')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                </svg>
                01614001687
              </a>
            </div>
            <a
              href="mailto:contactus@nolimitauto.co.uk"
              className="font-dm-sans text-white/40 text-sm hover:text-[#CC1A1A] transition-colors"
            >
              contactus@nolimitauto.co.uk
            </a>
          </div>
        </section>

        <GeoDivider />

        {/* ── BOOKING FORM SECTION ── */}
        {/* Trustpilot Mini Widget */}
        <div style={{ background: '#121212', padding: '48px 24px 0' }}>
          <div className="max-w-2xl mx-auto">
            <TrustpilotWidget templateId="53aa8807dec7e10d38f59f32" height="350px" />
          </div>
        </div>
        <section id="booking-form" className="py-20 px-6 relative z-10" style={{ background: '#121212' }}>
          <div className="max-w-2xl mx-auto">
            <div className="mb-10">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Book Online</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Book Your Coding Diagnostic
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mb-4" />
              <p className="font-dm-sans text-[#C0C0C0] text-base">
                Fill in the form below and a coding specialist will call you within 1 hour.
              </p>
            </div>

            {submitted ? (
              <div className="bg-[#1a1a1a] border border-[#CC1A1A]/40 rounded p-8 text-center">
                <div className="w-14 h-14 bg-[#CC1A1A]/10 border border-[#CC1A1A]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="2" strokeLinecap="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <p className="font-dm-sans font-bold text-white text-lg mb-2">Booking Received!</p>
                <p className="font-dm-sans text-[#C0C0C0] text-base">
                  Thank you! A coding specialist will call you within 1 hour.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Honeypot — hidden from real users, visible to bots */}
                <div
                  style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                  aria-hidden="true"
                >
                  <label htmlFor="website-hp-mc">Website</label>
                  <input
                    id="website-hp-mc"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={e => setFormData(p => ({ ...p, website: e.target.value }))}
                  />
                </div>
                {/* Name */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Full Name <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => handleFieldChange('name', e.target.value)}
                    onBlur={e => handleFieldBlur('name', e.target.value)}
                    placeholder="Your full name"
                    className={inputClass('name')}
                  />
                  {touched.name && fieldErrors.name && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.name}</p>
                  )}
                </div>
                {/* Phone */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Phone Number <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={e => handleFieldChange('phone', e.target.value)}
                    onBlur={e => handleFieldBlur('phone', e.target.value)}
                    placeholder="e.g. 07700 900000"
                    className={inputClass('phone')}
                  />
                  {touched.phone && fieldErrors.phone && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.phone}</p>
                  )}
                </div>
                {/* Email */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Email Address <span className="text-white/30 font-normal">(optional — for confirmation)</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => handleFieldChange('email', e.target.value)}
                    onBlur={e => handleFieldBlur('email', e.target.value)}
                    placeholder="your@email.com"
                    className={inputClass('email')}
                  />
                  {touched.email && fieldErrors.email && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.email}</p>
                  )}
                </div>
                {/* Registration */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Vehicle Registration <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.registration}
                    onChange={e => handleFieldChange('registration', e.target.value)}
                    onBlur={e => handleFieldBlur('registration', e.target.value)}
                    placeholder="e.g. AB12 CDE"
                    className={`${inputClass('registration')} uppercase`}
                  />
                  {touched.registration && fieldErrors.registration && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.registration}</p>
                  )}
                </div>
                {/* Vehicle Make/Model */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Vehicle Make &amp; Model <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.vehicleMake}
                    onChange={e => handleFieldChange('vehicleMake', e.target.value)}
                    onBlur={e => handleFieldBlur('vehicleMake', e.target.value)}
                    placeholder="e.g. Mercedes E-Class 2021"
                    className={inputClass('vehicleMake')}
                  />
                  {touched.vehicleMake && fieldErrors.vehicleMake && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.vehicleMake}</p>
                  )}
                </div>
                {/* Module/Coding Issue */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Module / Coding Issue <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <textarea
                    rows={4}
                    required
                    value={formData.codingIssue}
                    onChange={e => handleFieldChange('codingIssue', e.target.value)}
                    onBlur={e => handleFieldBlur('codingIssue', e.target.value)}
                    placeholder="Describe the fault or module you need coded/programmed..."
                    className={inputClass('codingIssue')}
                  />
                  {touched.codingIssue && fieldErrors.codingIssue && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.codingIssue}</p>
                  )}
                </div>

                {formError && (
                  <p className="font-dm-sans text-sm text-red-400 bg-red-900/20 border border-red-500/30 px-4 py-3 rounded-sm">{formError}</p>
                )}
                {rateLimitMsg && (
                  <p className="font-dm-sans text-sm text-yellow-400 bg-yellow-900/20 border border-yellow-500/30 px-4 py-3 rounded-sm">{rateLimitMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ background: '#CC1A1A', borderRadius: '3px' }}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 11-6.219-8.56"/>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>Book Diagnostic →</>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
